import { useEffect, useMemo, useState } from 'react';
import { MediaOption, MediaPosition, MediaRepeat, MediaSize, OpacityLevel } from '../constants';
import { themeValuesToStyle } from '../components/theme/constants';

interface UseMediaOverrideProps {
  mediaOverride?: File | null;
  pageId?: string;
  themeId?: string;
  themeRevision?: {
    media?: MediaOption;
    subTheme?: string;
    mediaRepeat?: MediaRepeat;
    mediaSize?: MediaSize;
    mediaPosition?: MediaPosition;
  };
  isEditing?: boolean;
  getOverrideSrc: (pageId: string, isEditing: boolean, cacheBuster: string) => string;
  getPresetSrc?: (themeId: string, subThemeId?: string) => string;
  opacity?: OpacityLevel;
}

const PRESET_MEDIA_OPTIONS = [MediaOption.presetSvg, MediaOption.presetImage, MediaOption.presetVideo] as const;

function isPresetMedia(media?: MediaOption): boolean {
  return media != null && (PRESET_MEDIA_OPTIONS as readonly MediaOption[]).includes(media);
}

export const useMediaOverride = ({
  mediaOverride,
  pageId,
  themeId,
  themeRevision,
  isEditing,
  getOverrideSrc,
  getPresetSrc,
  opacity,
}: UseMediaOverrideProps) => {
  const [overrideSrc, setOverrideSrc] = useState<string | undefined>(undefined);
  const [videoSrc, setVideoSrc] = useState<string | undefined>(undefined);
  const [cacheBuster, setCacheBuster] = useState<string>(() => Date.now().toString());

  useEffect(() => {
    let isActive = true;

    const setResolvedSource = async (src: string) => {
      try {
        const response = await fetch(src, { method: 'HEAD' });
        const contentType = response.headers.get('content-type') || '';

        if (!isActive) {
          return;
        }

        if (contentType.startsWith('video/')) {
          setOverrideSrc(undefined);
          setVideoSrc(src);
          return;
        }

        setOverrideSrc(src);
        setVideoSrc(undefined);
      } catch {
        if (!isActive) {
          return;
        }

        setOverrideSrc(src);
        setVideoSrc(undefined);
      }
    };

    if (mediaOverride) {
      const url = URL.createObjectURL(mediaOverride);

      if (mediaOverride.type.startsWith('video/')) {
        setOverrideSrc(undefined);
        setVideoSrc(url);
      } else {
        setOverrideSrc(url);
        setVideoSrc(undefined);
      }
      setCacheBuster(Date.now().toString());

      return () => {
        isActive = false;
        URL.revokeObjectURL(url);
      };
    } else if (themeId != null && themeRevision?.media === MediaOption.presetVideo && getPresetSrc != null) {
      setOverrideSrc(undefined);
      setVideoSrc(getPresetSrc(themeId, themeRevision?.subTheme));
    } else if (themeId != null && isPresetMedia(themeRevision?.media) && getPresetSrc != null) {
      setOverrideSrc(getPresetSrc(themeId, themeRevision?.subTheme));
      setVideoSrc(undefined);
    } else if (pageId != null && themeRevision?.media === MediaOption.custom && mediaOverride !== null) {
      const date = new Date().getTime().toString();
      const src = getOverrideSrc(pageId, !!isEditing, date);

      void setResolvedSource(src);
      setCacheBuster(date);
    } else {
      setOverrideSrc(undefined);
      setVideoSrc(undefined);
    }

    return () => {
      isActive = false;
    };
  }, [
    pageId,
    themeId,
    mediaOverride,
    themeRevision?.media,
    themeRevision?.subTheme,
    isEditing,
    getOverrideSrc,
    getPresetSrc,
  ]);

  const mediaRepeat = themeRevision?.mediaRepeat ?? MediaRepeat.repeat;
  const mediaSize = themeRevision?.mediaSize ?? MediaSize.cover;
  const mediaPosition = themeRevision?.mediaPosition ?? MediaPosition.center;

  const style = useMemo(
    () => themeValuesToStyle(overrideSrc, { mediaRepeat, mediaSize, mediaPosition, opacity }, cacheBuster),
    [overrideSrc, mediaRepeat, mediaSize, mediaPosition, opacity, cacheBuster]
  );

  return { overrideSrc, videoSrc, style };
};
